import React, { useState, useMemo } from "react";

const DEFAULT_SUGGESTIONS = [
  "AC",
  "Parking",
  "Stage",
  "Wi-Fi",
  "Power Backup",
  "Lawn",
  "Banquet Hall",
  "Indoor",
  "Outdoor",
  "Catering",
  "Decoration",
  "Sound System",
  "Projector",
  "Dance Floor",
  "Green Room",
  "Bridal Room",
  "Valet",
  "Generator",
  "Fireworks Allowed",
  "Pool",
  "Garden",
];

function parseAmenitiesString(str) {
  if (!str || typeof str !== "string") return [];
  return str
    .split(",")
    .map((s) => s.trim().replace(/^'|'$/g, "").trim())
    .filter(Boolean);
}

function joinAmenities(arr) {
  return arr.filter(Boolean).join(",");
}

export function AmenitiesChipInput({ value, onChange, error, placeholder, label, required, readOnly = false, suggestions: suggestionsProp }) {
  const chips = useMemo(() => parseAmenitiesString(value), [value]);
  const [inputVal, setInputVal] = useState("");
  const suggestions = Array.isArray(suggestionsProp) && suggestionsProp.length > 0 ? suggestionsProp : DEFAULT_SUGGESTIONS;

  // Suggestions that are not yet added – click adds to input and removes from this list
  const availableSuggestions = suggestions.filter(
    (s) =>
      !chips.includes(s) &&
      (inputVal === "" || s.toLowerCase().includes(inputVal.toLowerCase()))
  );

  if (readOnly) {
    return (
      <div className="form-group amenities-chip-wrap">
        <label className="form-label">
          {label} {required && <span className="required">*</span>}
        </label>
        <div className="amenities-chip-input amenities-chip-readonly">
          <div className="amenities-chips">
            {chips.length > 0 ? chips.map((chip, i) => (
              <span key={`${chip}-${i}`} className="amenity-chip">{chip}</span>
            )) : (
              <span className="amenities-chip-empty">—</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  const addChip = (item) => {
    if (!item || chips.includes(item)) return;
    const next = [...chips, item.trim()];
    onChange({ target: { name: "amenities", value: joinAmenities(next) } });
    setInputVal("");
  };

  const removeChip = (index) => {
    const next = chips.filter((_, i) => i !== index);
    onChange({ target: { name: "amenities", value: joinAmenities(next) } });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Backspace" && inputVal === "" && chips.length > 0) {
      removeChip(chips.length - 1);
      e.preventDefault();
    }
    if (e.key === "Enter" && inputVal.trim()) {
      addChip(inputVal.trim());
      e.preventDefault();
    }
  };

  return (
    <div className="form-group amenities-chip-wrap">
      <label className="form-label">
        {label} {required && <span className="required">*</span>}
      </label>
      <div className={`amenities-chip-input ${error ? "input-error" : ""}`}>
        <div className="amenities-chips">
          {chips.map((chip, i) => (
            <span key={`${chip}-${i}`} className="amenity-chip">
              {chip}
              <button
                type="button"
                className="amenity-chip-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  removeChip(i);
                }}
                aria-label={`Remove ${chip}`}
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            className="amenities-chip-field"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={chips.length === 0 ? placeholder : ""}
            autoComplete="off"
          />
        </div>
      </div>
      {availableSuggestions.length > 0 && (
        <div className="amenities-suggestions-list">
          <span className="amenities-suggestions-label">Click to add:</span>
          <div className="amenities-suggestions">
            {availableSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="amenities-suggestion-item"
                onClick={() => addChip(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && <span className="error-message">{error}</span>}
    </div>
  );
}
